import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, firstValueFrom, of, take, timeout } from "rxjs";
import { driver } from "driver.js";
import { LayoutService } from "../../layout/service/app.layout.service";
import { AuthService } from "./auth.service";
import { BackendService } from "./backend.service";

/**
 * A single step in the guided tour. Extends driver.js's step shape with an
 * optional `route` so the tour can walk the patient across pages
 * (e.g. into /appointments, /schedule and /billing) as they click Next/Back.
 */
interface TourStep {
  /** Router path this step lives on. Omit for steps that work on any page (e.g. sidebar). */
  route?: string;
  /** CSS selector for the element to spotlight. Omit for a centered popover. */
  element?: string;
  popover: {
    title: string;
    description: string;
    side?: "top" | "right" | "bottom" | "left" | "over";
    align?: "start" | "center" | "end";
  };
}

@Injectable({
  providedIn: "root",
})
export class TourService {
  private driverObj: ReturnType<typeof driver> | null = null;
  private steps: TourStep[] = [];
  private auto = false;
  private starting = false;
  private seenMarked = false;
  private positionWatchRaf = 0;
  private positionWatchUntil = 0;
  /** Whether the signed-in account has more than one linked client (family member). */
  private multiClient = false;
  private prevSidebar?: { anchored: boolean; sidebarActive: boolean };

  constructor(
    private router: Router,
    private layoutService: LayoutService,
    private authService: AuthService,
    private backendService: BackendService,
  ) {}

  /**
   * Start the guided tour.
   * @param opts.auto when true, the tour was auto-launched for a new patient
   *                  and completion is persisted to the backend on finish.
   */
  async start(opts: { auto?: boolean } = {}): Promise<void> {
    if (this.driverObj || this.starting) {
      return; // already running / starting
    }
    this.starting = true;
    try {
      this.auto = !!opts.auto;
      this.seenMarked = false;
      this.multiClient = await this.detectMultiClient();
      this.expandSidebar();
      this.steps = this.buildSteps();

      this.driverObj = driver({
        showProgress: true,
        allowClose: true,
        overlayColor: "rgba(15, 23, 42, 0.75)",
        stagePadding: 6,
        stageRadius: 10,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Done",
        steps: this.steps.map((s) => ({
          element: s.element,
          popover: s.popover,
        })),
        onNextClick: () => {
          void this.handleMove(1);
        },
        onPrevClick: () => {
          void this.handleMove(-1);
        },
        onCloseClick: () => this.finish(),
        onDestroyed: () => this.cleanup(),
      });

      this.driverObj.drive();
    } finally {
      this.starting = false;
    }
  }

  /**
   * Determine whether the account has multiple linked clients so the tour can
   * adapt its copy and steps (a client picker on booking, per-person balances
   * and per-person cards on billing). Defaults to single-client if claims are
   * unavailable.
   */
  private async detectMultiClient(): Promise<boolean> {
    try {
      const ids = await firstValueFrom(
        this.authService.getLinkedPatientIds().pipe(
          take(1),
          timeout({ first: 2000 }),
          catchError(() => of<string[]>([])),
        ),
      );
      return (ids?.length ?? 0) > 1;
    } catch {
      return false;
    }
  }

  /**
   * Handle Next/Back when steps may live on different routes. We take over the
   * default driver.js stepping: navigate if needed, wait for the target element
   * to render, then advance the highlight.
   */
  private async handleMove(direction: 1 | -1): Promise<void> {
    if (!this.driverObj) {
      return;
    }
    const currentIndex = this.driverObj.getActiveIndex() ?? 0;
    const targetIndex = currentIndex + direction;

    if (targetIndex < 0 || targetIndex >= this.steps.length) {
      this.finish();
      return;
    }

    const target = this.steps[targetIndex];
    const currentPath = this.router.url.split("?")[0];
    if (target.route && currentPath !== target.route) {
      await this.router.navigateByUrl(target.route);
    }
    if (target.element) {
      await this.waitForElement(target.element);
    }

    if (direction === 1) {
      this.driverObj.moveNext();
    } else {
      this.driverObj.movePrevious();
    }

    // Some pages render late content after the highlight is drawn — most
    // notably the outstanding-balance banner on /schedule and the async
    // balance card on /billing — which shifts the target element down.
    // Re-measure ONLY when the target actually moves, so ordinary steps don't
    // re-run driver's transition (that unconditional refreshing looked like
    // the highlight/popover bouncing).
    this.watchForShift(target.element);
  }

  /**
   * For a short window after a transition, watch the target element's position
   * and call driver.refresh() only when it actually moves. This keeps the
   * spotlight locked onto late-shifting content (e.g. an async balance banner)
   * without re-animating the highlight on steps where nothing changes.
   */
  private watchForShift(selector?: string): void {
    this.cancelPositionWatch();
    if (!selector) {
      return;
    }
    const initial = document.querySelector(selector)?.getBoundingClientRect();
    let lastTop = initial?.top ?? 0;
    let lastLeft = initial?.left ?? 0;
    this.positionWatchUntil = Date.now() + 1500;

    const tick = () => {
      if (!this.driverObj || Date.now() > this.positionWatchUntil) {
        this.positionWatchRaf = 0;
        return;
      }
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (
          Math.abs(rect.top - lastTop) > 1 ||
          Math.abs(rect.left - lastLeft) > 1
        ) {
          this.driverObj.refresh();
          // Re-baseline to the post-refresh position so driver's own
          // scroll-into-view doesn't immediately re-trigger another refresh.
          const after = el.getBoundingClientRect();
          lastTop = after.top;
          lastLeft = after.left;
        }
      }
      this.positionWatchRaf = requestAnimationFrame(tick);
    };
    this.positionWatchRaf = requestAnimationFrame(tick);
  }

  private cancelPositionWatch(): void {
    if (this.positionWatchRaf) {
      cancelAnimationFrame(this.positionWatchRaf);
      this.positionWatchRaf = 0;
    }
  }

  /** Resolve once the selector is in the DOM, or after a short timeout. */
  private waitForElement(selector: string, timeoutMs = 3000): Promise<void> {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (document.querySelector(selector) || Date.now() - start > timeoutMs) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  private finish(): void {
    this.driverObj?.destroy();
  }

  /**
   * Called by driver.js onDestroyed for every teardown path (Done, the close
   * button, or an overlay click). Restore layout and, for an auto-launched
   * tour, persist completion so it does not nag the patient again.
   */
  private cleanup(): void {
    if (this.auto && !this.seenMarked) {
      this.seenMarked = true;
      void this.markSeen();
    }
    this.cancelPositionWatch();
    this.restoreSidebar();
    this.driverObj = null;
  }

  private async markSeen(): Promise<void> {
    try {
      const token = await this.authService.getAuthTokenOnce();
      this.backendService.markTutorialSeen(token).subscribe({
        error: (err) => console.warn("Could not persist tutorial completion:", err),
      });
    } catch (err) {
      console.warn("Could not persist tutorial completion:", err);
    }
  }

  private expandSidebar(): void {
    this.prevSidebar = {
      anchored: this.layoutService.state.anchored,
      sidebarActive: this.layoutService.state.sidebarActive,
    };
    // Keep the sidebar pinned open so menu labels stay visible while we
    // spotlight individual nav items.
    this.layoutService.state.anchored = true;
    this.layoutService.state.sidebarActive = true;
  }

  private restoreSidebar(): void {
    if (this.prevSidebar) {
      this.layoutService.state.anchored = this.prevSidebar.anchored;
      this.layoutService.state.sidebarActive = this.prevSidebar.sidebarActive;
      this.prevSidebar = undefined;
    }
  }

  /**
   * The tour script: sidebar orientation, then a guided walk into the
   * Appointments page → Book appointment → the booking flow, and finally a
   * walk through Billing & Payments. Copy and a couple of steps adapt to
   * whether the account has one client or several linked family members.
   */
  private buildSteps(): TourStep[] {
    const multi = this.multiClient;

    const steps: TourStep[] = [
      {
        popover: {
          title: "Welcome to Brightside 👋",
          description:
            "Here's a quick tour of your patient portal. We'll show you around, then walk through booking an appointment and managing billing. You can exit any time.",
          align: "center",
        },
      },
      {
        element: ".layout-menu-container",
        popover: {
          title: "Your menu",
          description:
            "This sidebar is how you get around the portal. Let's look at each section.",
          side: "right",
          align: "start",
        },
      },
      {
        element: '[data-tour="home"]',
        popover: {
          title: "Home",
          description:
            "Your dashboard — upcoming appointments, balances, and quick actions at a glance.",
          side: "right",
        },
      },
      {
        element: '[data-tour="documents"]',
        popover: {
          title: "Documents",
          description:
            "Forms and documents your care team sends you to review, sign, or upload.",
          side: "right",
        },
      },
      {
        element: '[data-tour="insurance"]',
        popover: {
          title: "Insurance",
          description:
            "Add or update your insurance details and upload your insurance card.",
          side: "right",
        },
      },
      {
        element: '[data-tour="personal-info"]',
        popover: {
          title: "Personal Information",
          description:
            "Keep your contact info, emergency contacts, and notification preferences up to date.",
          side: "right",
        },
      },

      // ── Appointments walkthrough ─────────────────────────────────────────
      {
        element: '[data-tour="appointments"]',
        popover: {
          title: "Appointments",
          description: "Let's open the Appointments page to see how it works.",
          side: "right",
        },
      },
      {
        route: "/appointments",
        element: '[data-tour="appointments-list"]',
        popover: {
          title: "Your appointments",
          description:
            "Your upcoming and past appointments show in these tabs. From here you can review details, reschedule, or cancel.",
          side: "bottom",
          align: "start",
        },
      },
      {
        route: "/appointments",
        element: '[data-tour="appointments-book"]',
        popover: {
          title: "Book a new appointment",
          description:
            "When you're ready for a new visit, click Book appointment. Let's go there now.",
          side: "bottom",
          align: "end",
        },
      },
    ];

    // ── Booking flow (client picker only when multiple are linked) ──────────
    if (multi) {
      steps.push({
        route: "/schedule",
        element: '[data-tour="schedule-client"]',
        popover: {
          title: "Choose who it's for",
          description:
            "Because more than one person is on your account, start by picking which client this appointment is for.",
          side: "bottom",
          align: "start",
        },
      });
    }

    steps.push({
      route: "/schedule",
      element: '[data-tour="schedule-clinician"]',
      popover: {
        title: "Booking an appointment",
        description: multi
          ? "After choosing the client, pick a clinician here, then a date on the calendar, then a time slot. We'll confirm everything before booking."
          : "Booking takes three quick steps: pick a clinician here, choose an available date on the calendar, then tap a time slot. We'll confirm the details before anything is booked.",
        side: "bottom",
        align: "start",
      },
    });

    // ── Billing & Payments walkthrough ──────────────────────────────────────
    steps.push(
      {
        element: '[data-tour="billing"]',
        popover: {
          title: "Billing & Payments",
          description: "Now let's look at billing. Opening it up…",
          side: "right",
        },
      },
      {
        route: "/billing",
        element: '[data-tour="billing-balance"]',
        popover: {
          title: "Your balance",
          description: multi
            ? "This shows your family's combined balance, with a breakdown per person — each has its own “Make a payment” button when a payment is due."
            : "Your current balance shows here. When you owe a payment, a “Make a payment” button appears on this card so you can pay securely by card.",
          side: "bottom",
          align: "start",
        },
      },
      {
        route: "/billing",
        element: '[data-tour="billing-payment-methods"]',
        popover: {
          title: "Payment methods",
          description: multi
            ? "Saved cards are organized by family member — each person has their own cards. Scroll here to manage them."
            : "Your saved cards live here. This is also where you'll set up payments.",
          side: "top",
          align: "start",
        },
      },
      {
        route: "/billing",
        element: '[data-tour="billing-add-card"]',
        popover: {
          title: "Add or update a card",
          description: multi
            ? "Add a card with “Add new card” (you'll choose the family member). To set a default or remove a card, use the actions on each card."
            : "Add a card with “Add new card.” To set a default or remove a card, use the actions shown on each saved card.",
          side: "bottom",
          align: "end",
        },
      },
      {
        route: "/billing",
        element: '[data-tour="billing-autopay"]',
        popover: {
          title: "Auto Pay",
          description:
            "Turn on automatic payments here so your bills are paid on time without you having to remember.",
          side: "bottom",
          align: "end",
        },
      },
    );

    // ── Replay ──────────────────────────────────────────────────────────────
    steps.push({
      element: ".sidebar-tutorial-btn",
      popover: {
        title: "Replay anytime",
        description:
          "That's the tour! You can replay it whenever you like by clicking Tutorial at the bottom of the menu.",
        side: "right",
      },
    });

    return steps;
  }
}
